import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, type RowComponentProps } from 'react-window';
import { ItemReadOnly } from '../components/ItemReadOnly.tsx';
import { StyledListItemText } from '../components/ListItemText.styled.tsx';
import { StyledMuiList } from '../components/MuiList.styled.tsx';
import { StyledVirtualListContainerDiv } from '../components/VirtualListContainerDiv.styled.tsx';
import type { ActionType, BaseListOption, CustomItemRenderProps, EnrichedListOption } from '../types.ts';
import { getPaddedRowHeight, offsetListRowStyle } from '../utils/utils.ts';
import { HEADER_GUTTER_SIZE, ITEM_SIZE, OVERSCAN_EXTRA_ITEMS, READ_ONLY_LIST_HEIGHT } from './constants.ts';

type ReadOnlyCustomItemRender<T> = (
  props: Omit<CustomItemRenderProps<T>, 'handleRowClick' | 'isDisabled'> & {
    actionType: ActionType;
    disableTouchRipple: boolean;
    showExpirationAlerts: boolean;
  }
) => React.ReactNode;

export type VirtualizedReadOnlyFlatListProps<T> = {
  data: T[];
  searchInput: string;
  actionType: ActionType;
  customItem?: ReadOnlyCustomItemRender<T>;
  itemSize?: number;
};

type ReadOnlyFlatListRowProps = {
  data: EnrichedListOption<BaseListOption>[];
  searchInput: string;
  actionType: ActionType;
  customItem?: ReadOnlyCustomItemRender<EnrichedListOption<BaseListOption>>;
  itemSize: number;
};

const getReadOnlyFlatListRowHeight = (index: number, { itemSize, data }: ReadOnlyFlatListRowProps) =>
  getPaddedRowHeight(itemSize, index, data.length);

const getReadOnlyFlatListRowKey = (index: number, { data }: ReadOnlyFlatListRowProps) => data[index].id;

function ReadOnlyFlatListRow({
  index,
  style,
  data,
  searchInput,
  actionType,
  customItem,
  itemSize,
}: RowComponentProps<ReadOnlyFlatListRowProps>) {
  const value = data[index];
  const rowStyle = offsetListRowStyle(style, itemSize);

  if (customItem) {
    return (
      <>
        {customItem({
          item: value,
          style: rowStyle,
          searchInput,
          actionType,
          disableTouchRipple: true,
          hideActionIcon: true,
          showExpirationAlerts: true,
        })}
      </>
    );
  }

  return <ItemReadOnly actionType={actionType} name={value.name} searchInput={searchInput} style={rowStyle} />;
}

export const VirtualizedReadOnlyFlatList = <T extends EnrichedListOption<BaseListOption>>({
  data,
  searchInput,
  actionType,
  customItem,
  itemSize,
}: VirtualizedReadOnlyFlatListProps<T>) => {
  const { t } = useTranslation();
  const itemSizeValue = itemSize ?? ITEM_SIZE;
  const [initialListSize] = useState(() => {
    const totalHeight = data.length * itemSizeValue + HEADER_GUTTER_SIZE;

    if (totalHeight >= READ_ONLY_LIST_HEIGHT) {
      return READ_ONLY_LIST_HEIGHT;
    }

    return totalHeight;
  });

  return (
    <>
      {data.length ? (
        <StyledVirtualListContainerDiv hasError={false}>
          <List
            data-test="items-list"
            rowComponent={ReadOnlyFlatListRow}
            rowCount={data.length}
            rowProps={{
              data,
              searchInput,
              actionType,
              customItem: customItem as ReadOnlyCustomItemRender<EnrichedListOption<BaseListOption>> | undefined,
              itemSize: itemSizeValue,
            }}
            rowHeight={getReadOnlyFlatListRowHeight}
            rowKey={getReadOnlyFlatListRowKey}
            overscanCount={OVERSCAN_EXTRA_ITEMS}
            style={{ height: initialListSize, width: '100%' }}
          />
        </StyledVirtualListContainerDiv>
      ) : (
        <StyledMuiList component="div" role="list" listHeight={initialListSize} hasError={false}>
          <StyledListItemText
            primary={t('No items.')}
            isSelected={true}
            disableTypography
            marginRight={16}
            marginLeft={16}
          />
        </StyledMuiList>
      )}
    </>
  );
};
